// React Component For Login Form 

import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import bandtango_logo from './bandtango.png'; // Adjust the path as necessary
import useUserStore from './UserStore';
import { apiUrl } from './config';

function LoginForm() {
    const navigate = useNavigate();

    // Use the correct methods from UserStore
    const setUser = useUserStore((state) => state.setUser);
    const clearUser = useUserStore((state) => state.clearUser);

    const getProfileByEmail = async (email) => {
        if (!email) {
            return {};
        }

        const profileResponse = await fetch(apiUrl(`/profile/email/${encodeURIComponent(email)}`), {
            method: 'GET',
        });

        if (!profileResponse.ok) {
            return {};
        }

        return await profileResponse.json();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const response = await fetch(apiUrl('/auth/login'), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                email: e.target.email.value,
                password: e.target.password.value,
            }),
        });
        
        if (response.ok) {
            const authUserData = await response.json();
            const profileData = await getProfileByEmail(e.target.email.value);
            const userData = {
                ...profileData,
                authResponse: authUserData,
            };
            console.log('Login succeeded with email/password');
            setUser(userData); // This will set isAuthorized to true
            navigate('/'); // Navigate to dashboard
        } else {
            console.log('Login failed');
            clearUser(); // This will set isAuthorized to false
            // Stay on login page, maybe show error message
        }
    };

    const onSuccess = async (credentialResponse) => {
        try {
            const decodedToken = jwtDecode(credentialResponse.credential);
            console.log('Google login token decoded:', decodedToken);

            const response = await fetch(apiUrl('/auth/login'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(decodedToken),
            });
            
            if (response.ok) {
                const authResponse = await response.json();
                const profileData = await getProfileByEmail(decodedToken.email);
                const userData = {
                    ...profileData,
                    authResponse,
                    googleAuthResponse: credentialResponse,
                };
                console.log('Google login succeeded');
                console.dir(userData);
                
                // Set user data (this automatically sets isAuthorized to true)
                setUser(userData);
                
                console.log('User state isAuthorized:', useUserStore.getState().isAuthorized);
                navigate('/'); // Navigate to dashboard
            } else {
                console.log('Google login failed');
                clearUser(); // This sets isAuthorized to false
            }
        } catch (error) {
            console.error('Error during Google login:', error);
            clearUser();
        }
    };

    const onError = () => {
        console.log('Google Login Failed');
        clearUser();
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div>
                    <img
                        className="mx-auto h-24 w-auto"
                        src={bandtango_logo}
                        alt="Bandtango"
                    />
                </div>
                <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                    <input type="hidden" name="remember" defaultValue="true" />
                    <div className="rounded-md shadow-sm">
                        <div>
                            <label htmlFor="email-address" className="sr-only">
                                Email address
                            </label>
                            <input
                                id="email-address"
                                name="email"
                                type="email"
                                autoComplete="email"
                                required
                                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:z-10 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
                                placeholder="Email address"
                            />
                        </div>
                        <div>
                            <label htmlFor="password" className="sr-only">
                                Password
                            </label>
                            <input
                                id="password"
                                name="password"
                                type="password"
                                autoComplete="current-password"
                                required
                                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:z-10 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
                                placeholder="Password"
                            />
                        </div>
                    </div>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <input
                                id="remember-me"
                                name="remember-me"
                                type="checkbox"
                                className="h-4 w-4 rounded-none border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                                Remember me
                            </label>
                        </div>
                        <div className="text-sm">
                            <a href="/forgot-password" className="font-medium text-indigo-600 hover:text-indigo-500">
                                Forgot your password?
                            </a>
                        </div>
                    </div>

                    <div>
                        <button
                            type="submit"
                            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                            Sign in
                        </button>
                    </div>
                </form>
                <div className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                    <GoogleLogin  
                        onSuccess={onSuccess}
                        onError={onError}
                    />
                </div>
            </div>
        </div>
    );
}

export default LoginForm;